import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, Badge, useToast } from '@hiring-intelligence/ui';
import { X, Search, Users } from 'lucide-react';
import { hiringApi, usersApi } from '@/services/api';

interface ShareDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	hiringRequestId: string;
	currentSharedTo: string[];
	currentUserId: string;
	onSuccess: () => void;
}

export function ShareDialog({ open, onOpenChange, hiringRequestId, currentSharedTo, currentUserId, onSuccess }: ShareDialogProps) {
	const { toast } = useToast();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	const { data: usersData } = useQuery({
		queryKey: ['users-shareable'],
		queryFn: () => usersApi.listShareable(),
		enabled: open
	});

	// Initialize selected IDs from current shared list when dialog opens
	useEffect(() => {
		if (open) {
			setSelectedIds([...currentSharedTo]);
			setSearchQuery('');
		}
	}, [open, currentSharedTo]);

	const allUsers = (usersData?.data || []).filter((u: any) => u.id !== currentUserId && !u.isBlocked);
	const alreadyInvitedUsers = allUsers.filter((u: any) => currentSharedTo.includes(u.id) && selectedIds.includes(u.id));
	const availableUsers = allUsers.filter((u: any) => !selectedIds.includes(u.id));
	const filteredUsers = availableUsers.filter((u: any) =>
		u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
		u.email?.toLowerCase().includes(searchQuery.toLowerCase())
	);

	const toggleUser = (userId: string) => {
		setSelectedIds(prev =>
			prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
		);
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			const toAdd = selectedIds.filter(id => !currentSharedTo.includes(id));
			const toRemove = currentSharedTo.filter(id => !selectedIds.includes(id));

			if (toAdd.length > 0) {
				await hiringApi.share(hiringRequestId, toAdd);
			}
			if (toRemove.length > 0) {
				await hiringApi.removeShare(hiringRequestId, toRemove);
			}

			toast({ title: 'Updated', description: 'Sharing settings updated' });
			onSuccess();
			onOpenChange(false);
		} catch (error: any) {
			toast({ title: 'Error', description: error.message || 'Failed to update sharing', variant: 'destructive' });
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Users className="h-5 w-5" />
						Share with Team
					</DialogTitle>
				</DialogHeader>

				{/* Search */}
				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<input
						type="text"
						placeholder="Search by name or email..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-background"
					/>
				</div>

				{/* Already invited */}
				{alreadyInvitedUsers.length > 0 && (
					<div>
						<p className="text-xs text-muted-foreground mb-1.5">Already invited</p>
						<div className="flex flex-wrap gap-1.5">
							{alreadyInvitedUsers.map((user: any) => (
								<Badge key={user.id} variant="secondary" className="flex items-center gap-1 pr-1">
									{user.name}
									<button onClick={() => {
										setSelectedIds(prev => prev.filter(id => id !== user.id));
									}} className="ml-0.5 hover:text-destructive">
										<X className="h-3 w-3" />
									</button>
								</Badge>
							))}
						</div>
					</div>
				)}

				{/* Newly selected badges */}
				{selectedIds.filter(id => !currentSharedTo.includes(id)).length > 0 && (
					<div>
						<p className="text-xs text-muted-foreground mb-1.5">New invites</p>
						<div className="flex flex-wrap gap-1.5">
							{selectedIds.filter(id => !currentSharedTo.includes(id)).map(uid => {
								const user = allUsers.find((u: any) => u.id === uid);
								return user ? (
									<Badge key={uid} variant="default" className="flex items-center gap-1 pr-1">
										{user.name}
										<button onClick={() => toggleUser(uid)} className="ml-0.5 hover:text-destructive">
											<X className="h-3 w-3" />
										</button>
									</Badge>
								) : null;
							})}
						</div>
					</div>
				)}

				{/* User list */}
				<div className="max-h-60 overflow-y-auto border rounded-md divide-y">
					{filteredUsers.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-4">No users found</p>
					) : (
						filteredUsers.map((user: any) => (
							<button
								key={user.id}
								type="button"
								onClick={() => toggleUser(user.id)}
								className="flex items-center gap-3 px-3 py-2 w-full text-left cursor-pointer hover:bg-muted/50 transition-colors"
							>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{user.name}</p>
									<p className="text-xs text-muted-foreground truncate">{user.email}</p>
								</div>
								{user.role && (
									<Badge variant="outline" className="text-[10px] shrink-0">{user.role}</Badge>
								)}
							</button>
						))
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
					<Button onClick={handleSave} disabled={isSaving}>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
